import type {
  TemplateWindowAppearance,
  TextureAppearance,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';

export type TemplatePaletteConfig = {
  paletteName?: string;
  wallAppearances?: {
    walls: TextureAppearance;
    trims?: TextureAppearance;
  }[];
  trimAppearances?: TextureAppearance[];
  roofAppearances?: TextureAppearance[];
  windowAppearances?: TemplateWindowAppearance[];
  doorAppearances?: { url: string }[];
};

const palette1 = {
  paletteName: 'Arctic Chill',
  wallAppearances: [
    {
      walls: {
        color: '#e2e3dd',
      },
      trims: {
        color: '#e2e3dd',
      },
    },
  ],
  roofAppearances: [
    {
      textureId: 556,
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/striking/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/striking/garage.glb' },
  ],
};

const palette2 = {
  paletteName: 'Sleek Onyx',
  wallAppearances: [
    {
      walls: {
        color: '#e2e3dd',
      },
      trims: {
        color: '#494e4b',
      },
    },
  ],
  roofAppearances: [
    {
      textureId: 556,
    },
  ],
  doorAppearances: [
    {
      url: 'https://machete-terrain.hover.to/models/door/minimalist/entry.glb',
    },
    {
      url: 'https://machete-terrain.hover.to/models/door/minimalist/garage.glb',
    },
  ],
};

const palette4 = {
  paletteName: 'Dark Earth',
  wallAppearances: [
    {
      walls: {
        color: '#494e4b',
      },
      trims: {
        color: '#494e4b',
      },
    },
  ],
  roofAppearances: [
    {
      textureId: 556,
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/bold/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/bold/garage.glb' },
  ],
};

const palette5 = {
  paletteName: 'Misty Morning',
  wallAppearances: [
    {
      walls: {
        color: '#6f7a7d',
      },
      trims: {
        color: '#e2e3dd',
      },
    },
  ],
  roofAppearances: [
    {
      textureId: 556,
    },
  ],
  doorAppearances: [
    {
      url: 'https://machete-terrain.hover.to/models/door/minimalist/entry.glb',
    },
    {
      url: 'https://machete-terrain.hover.to/models/door/minimalist/garage.glb',
    },
  ],
};

const palette6 = {
  paletteName: 'Minimalist Grey',
  wallAppearances: [
    {
      walls: {
        color: '#b2afac',
      },
      trims: {
        color: '#e2e3dd',
      },
    },
  ],
  roofAppearances: [
    {
      textureId: 564,
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/robust/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/robust/garage.glb' },
  ],
};

const palette7 = {
  paletteName: 'Desert Sands',
  wallAppearances: [
    {
      walls: {
        color: '#887b67',
      },
      trims: {
        color: '#e2e3dd',
      },
    },
  ],
  roofAppearances: [
    {
      textureId: 560,
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/decorated/entry.glb' },
    {
      url: 'https://machete-terrain.hover.to/models/door/decorated/garage.glb',
    },
  ],
};

const palette8 = {
  paletteName: 'Soft Sandstone',
  wallAppearances: [
    {
      walls: {
        color: '#6a6651',
      },
      trims: {
        color: '#e2e3dd',
      },
    },
  ],
  roofAppearances: [
    {
      textureId: 560,
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/elegant/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/elegant/garage.glb' },
  ],
};

const palette9 = {
  paletteName: 'Gentle Grey',
  wallAppearances: [
    {
      walls: {
        color: '#b4b7b3',
      },
      trims: {
        color: '#e2e3dd',
      },
    },
  ],
  roofAppearances: [
    {
      textureId: 569,
      color: '#6f777e',
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/rustic/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/rustic/garage.glb' },
  ],
};

const palette10 = {
  paletteName: 'Antique Ivory',
  roofAppearances: [{ textureId: 565 }],
  wallAppearances: [
    {
      walls: {
        color: '#e0d5be',
      },
      trims: {
        color: '#e2e3dd',
      },
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/decorated/entry.glb' },
    {
      url: 'https://machete-terrain.hover.to/models/door/decorated/garage.glb',
    },
  ],
};

const palette11 = {
  paletteName: 'Muted Olive',
  roofAppearances: [{ textureId: 560 }],
  wallAppearances: [
    {
      walls: {
        color: '#a8aa9c',
      },
      trims: {
        color: '#e2e3dd',
      },
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/striking/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/striking/garage.glb' },
  ],
};

const palette12 = {
  paletteName: 'Subtle Moss',
  roofAppearances: [{ textureId: 568 }],
  wallAppearances: [
    {
      walls: {
        color: '#a4a192',
      },
      trims: {
        color: '#c8bfb2',
      },
    },
    {
      walls: {
        color: '#747771',
      },
    },
    {
      walls: {
        color: '#a4a192',
      },
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/elegant/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/elegant/garage.glb' },
  ],
};

const palette13 = {
  paletteName: 'Winter Breeze',
  roofAppearances: [{ textureId: 556 }],
  wallAppearances: [
    {
      walls: {
        color: '#e2e3dd',
      },
      trims: {
        color: '#494e4b',
      },
    },
    {
      walls: {
        color: '#494e4b',
      },
    },
    {
      walls: {
        color: '#e2e3dd',
      },
    },
  ],
  doorAppearances: [
    {
      url: 'https://machete-terrain.hover.to/models/door/adventurous/entry.glb',
    },
    {
      url: 'https://machete-terrain.hover.to/models/door/adventurous/garage.glb',
    },
  ],
};

const palette14 = {
  paletteName: 'Midnight Mist',
  roofAppearances: [{ textureId: 556 }],
  wallAppearances: [
    {
      walls: {
        color: '#494e4b',
      },
      trims: {
        color: '#494e4b',
      },
    },
    {
      walls: {
        color: '#e2e3dd',
      },
    },
    {
      walls: {
        color: '#494e4b',
      },
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/rustic/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/rustic/garage.glb' },
  ],
};

const palette15 = {
  paletteName: 'Rustic Tones',
  roofAppearances: [{ textureId: 565 }],
  wallAppearances: [
    {
      walls: {
        color: '#b2afac',
      },
      trims: {
        color: '#e2e3dd',
      },
    },
    {
      walls: {
        color: '#7c7874',
      },
    },
    {
      walls: {
        color: '#b2afac',
      },
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/robust/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/robust/garage.glb' },
  ],
};

const palette16 = {
  paletteName: 'Driftless Valley',
  roofAppearances: [{ textureId: 560 }],
  wallAppearances: [
    {
      walls: {
        color: '#666457',
      },
      trims: {
        color: '#494e4b',
      },
    },
    {
      walls: {
        color: '#494e4b',
      },
    },
    {
      walls: {
        color: '#5d5a4d',
      },
    },
  ],
  doorAppearances: [
    { url: 'https://machete-terrain.hover.to/models/door/bold/entry.glb' },
    { url: 'https://machete-terrain.hover.to/models/door/bold/garage.glb' },
  ],
};

export const SIMPLE_PALETTES: TemplatePaletteConfig[] = [
  palette1,
  palette2,
  palette4,
  palette5,
  palette6,
  palette7,
  palette8,
  palette9,
  palette10,
  palette11,
];

export const ACCENTED_PALETTES: TemplatePaletteConfig[] = [
  palette12,
  palette13,
  palette14,
  palette15,
  palette16,
];
